import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Star, ExternalLink, ChevronRight } from 'lucide-react';
import Navbar from '../../components/navbar/Navbar';
import Footer from '../../components/footer/Footer';
import CommunityReviewSection from '../../components/CommunityReviewSection';
import SimilarProducts from '../../components/SimilarProducts';
import { useData } from '../../context/DataContext';
import { generateProductSlug, generateBrandedProductSlug } from '../../utils/slugUtils';

// Container component
const Container = ({ children, className = "" }) => (
    <div className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
);

const ProductDetailPage = () => {
    const { productSlug } = useParams();
    const navigate = useNavigate();
    const { products, categories, subcategories } = useData();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expandedSections, setExpandedSections] = useState({
        highlights: false,
        proscons: false,
        ingredients: false
    });

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    // Get similar products based on category and subcategory
    const similarProducts = useMemo(() => {
        if (!product || !products) return [];
        
        const productCategory = typeof product.category === 'string' 
            ? product.category 
            : product.category?.name;
        const productSubcategory = typeof product.subcategory === 'string'
            ? product.subcategory
            : product.subcategory?.name;
        
        // Filter products from same category/subcategory
        return products.filter(p => {
            if (!p.isActive || p._id === product._id) return false;
            
            const pCategory = typeof p.category === 'string' ? p.category : p.category?.name;
            const pSubcategory = typeof p.subcategory === 'string' ? p.subcategory : p.subcategory?.name;
            
            // Prefer same subcategory, fallback to same category
            if (productSubcategory && pSubcategory === productSubcategory) return true;
            if (productCategory && pCategory === productCategory) return true;
            
            return false;
        }).slice(0, 12); // Limit to 12 products
    }, [product, products]);

    useEffect(() => {
        if (products && products.length > 0) {
            // Try to find product by matching branded slug (brand + product name)
            const foundProduct = products.find(p => {
                const brandedSlug = generateBrandedProductSlug(p.brand, p.name);
                return brandedSlug === productSlug;
            });
            
            // Fallback 1: try product name only (for old links)
            const productByName = foundProduct || products.find(p => {
                const productSlugFromName = generateProductSlug(p.name);
                return productSlugFromName === productSlug;
            });
            
            // Fallback 2: try to find by ID if slug doesn't match (backwards compatibility)
            const productById = productByName || products.find(p => p._id === productSlug);
            
            setProduct(productById);
            setLoading(false);
        }
    }, [products, productSlug]);

    if (loading) {
        return (
            <div className="min-h-screen bg-[#f8f8f8]">
                <Navbar />
                <div className="flex items-center justify-center min-h-[60vh]">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ab3d22] mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading product...</p>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    if (!product) {
        return (
            <div className="min-h-screen bg-[#f8f8f8]">
                <Navbar />
                <div className="flex items-center justify-center min-h-[60vh]">
                    <div className="text-center">
                        <h1 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h1>
                        <p className="text-gray-600 mb-6">The product you're looking for doesn't exist or has been removed.</p>
                        <button
                            onClick={() => navigate(-1)}
                            className="bg-[#ab3d22] text-white px-6 py-2 rounded-lg hover:bg-[#8a2f1a] transition-colors"
                        >
                            Go Back
                        </button>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    const formatPrice = (price) => {
        if (typeof price === 'number') {
            return `$${price.toFixed(2)}`;
        }
        return price || 'Price not available';
    };

    const renderStars = (rating) => {
        const numRating = Number(rating) || 0;
        const fullStars = Math.floor(numRating);
        const hasHalfStar = numRating % 1 >= 0.3; // Show half star for .3 and above
        const stars = [];

        // Full stars
        for (let i = 0; i < fullStars; i++) {
            stars.push(
                <Star
                    key={`full-${i}`}
                    className="h-5 w-5 text-[#ab3d22] fill-current"
                />
            );
        }

        // Half star (shown as partially filled)
        if (hasHalfStar && fullStars < 5) {
            stars.push(
                <div key="half" className="relative">
                    <Star className="h-5 w-5 text-gray-300" />
                    <div className="absolute inset-0 overflow-hidden" style={{ width: '50%' }}>
                        <Star className="h-5 w-5 text-[#ab3d22] fill-current" />
                    </div>
                </div>
            );
        }

        // Empty stars
        const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
        for (let i = 0; i < emptyStars; i++) {
            stars.push(
                <Star
                    key={`empty-${i}`}
                    className="h-5 w-5 text-gray-300"
                />
            );
        }

        return stars;
    };

    const createBrandSlug = (brandName) => {
        return brandName.toLowerCase().replace(/[^a-z0-9]/g, '-');
    };

    // Get breadcrumbs
    const getBreadcrumbs = () => {
        if (!product) return [];
        
        const breadcrumbs = [
            { label: 'Home', link: '/' }
        ];
        
        // Find category
        if (product.category) {
            const category = categories?.find(c => 
                c._id === product.category || c._id === product.category._id || c.name === product.category
            );
            if (category) {
                breadcrumbs.push({ 
                    label: category.name, 
                    link: `/category/${category.slug || category.name.toLowerCase()}` 
                });
            }
        }
        
        // Find subcategory
        if (product.subcategory) {
            const subcategory = subcategories?.find(s => 
                s._id === product.subcategory || s._id === product.subcategory._id || s.name === product.subcategory
            );
            if (subcategory) {
                const categorySlug = typeof product.category === 'object' && product.category.slug 
                    ? product.category.slug 
                    : categories?.find(c => c._id === product.category)?.slug || 'category';
                breadcrumbs.push({ 
                    label: subcategory.name, 
                    link: `/category/${categorySlug}/${subcategory.slug || subcategory.name.toLowerCase()}` 
                });
            }
        }
        
        // Add brand
        if (product.brand) {
            breadcrumbs.push({ 
                label: product.brand, 
                link: `/products?brand=${encodeURIComponent(product.brand)}` 
            });
        }
        
        breadcrumbs.push({ label: product.name, link: null });
        
        return breadcrumbs;
    };

    return (
        <div className="min-h-screen bg-[#f8f8f8]">
            <Navbar />
            
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Breadcrumbs */}
                <nav className="mb-6 text-sm text-gray-600" aria-label="Breadcrumb">
                    <ol className="flex flex-wrap items-center gap-2">
                        {getBreadcrumbs().map((crumb, i) => (
                            <li key={i} className="flex items-center gap-2">
                                {crumb.link ? (
                                    <Link to={crumb.link} className="hover:text-[#ab3d22] transition-colors">
                                        {crumb.label}
                                    </Link>
                                ) : (
                                    <span className="text-gray-900 font-medium">{crumb.label}</span>
                                )}
                                {i < getBreadcrumbs().length - 1 && <ChevronRight className="h-3 w-3 text-gray-400" />}
                            </li>
                        ))}
                    </ol>
                </nav>

                {/* Brand and Product Name - Outside Container */}
                <div className="mb-6">
                    <h1 className="text-4xl font-bold leading-tight" style={{fontFamily: 'Poppins, sans-serif', fontWeight: 700}}>
                        <Link
                            to={`/products?brand=${encodeURIComponent(product.brand)}`}
                            className="text-gray-900 hover:text-[#ab3d22] transition-colors"
                        >
                            {product.brand || 'BRAND'}
                        </Link>
                        <span className="text-gray-700"> {product.name}</span>
                    </h1>
                </div>

                {/* Main Product Container */}
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden relative">
                    {/* Top Rated Badge - Top Right of Container */}
                    {product.badges?.includes('top_rated') && (
                        <div className="absolute top-4 right-4 z-10 inline-flex items-center px-4 py-2 bg-[#5a8a8c] text-white rounded-full text-sm font-semibold shadow-md">
                            Top Rated
                        </div>
                    )}
                    
                    {/* Top Section: Image + Product Details */}
                    <div className="grid lg:grid-cols-2 gap-8 p-8 pt-16 lg:pt-8">

                        {/* LEFT COLUMN: Product Image & Specs */}
                        <div className="space-y-6">
                        {/* Product Image */}
                            <div className="flex items-start justify-center">
                                <div className="bg-white rounded-xl p-6 border border-gray-100 w-full">
                                <img
                                    src={product.imageUrl || '/placeholder.png'}
                                    alt={product.name}
                                        className="w-full h-80 object-contain"
                                />
                            </div>
                        </div>

                            {/* Product Info Grid */}
                            <div className="bg-white rounded-lg p-5 border border-gray-100">
                                <h4 className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-4">Product Details</h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                {/* Best For */}
                                {product.suitability && product.suitability.length > 0 && (
                                    <div>
                                        <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Best For</h3>
                                        <p className="text-gray-700 font-normal text-sm">
                                            {product.suitability.map((suit, index) => {
                                                const suitText = typeof suit === 'string' ? suit : suit?.name || '';
                                                return suitText;
                                            }).filter(Boolean).join(' · ')}
                                        </p>
                                    </div>
                                )}

                                {/* Key Ingredient */}
                            {product.keyIngredients && product.keyIngredients.length > 0 && (
                                    <div>
                                            <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Key Ingredient</h3>
                                            <p className="text-gray-700 font-normal text-sm">
                                            {(() => {
                                                const firstIngredient = product.keyIngredients[0];
                                                if (typeof firstIngredient === 'string') {
                                                    return firstIngredient.split(/\s*[→:]\s*/)[0];
                                                } else if (firstIngredient && typeof firstIngredient === 'object' && firstIngredient.name) {
                                                    return firstIngredient.name;
                                                }
                                                return 'Premium Formula';
                                            })()}
                                        </p>
                                    </div>
                                )}

                                {/* Benefits */}
                                {product.benefits && product.benefits.length > 0 && (
                                    <div>
                                            <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Benefits</h3>
                                            <p className="text-gray-700 font-normal text-sm">
                                            {(() => {
                                                const benefitsList = Array.isArray(product.benefits) ? product.benefits : [product.benefits];
                                                const cleanedBenefits = benefitsList
                                                    .flatMap(b => typeof b === 'string' ? b.split('•').filter(x => x.trim()) : [b])
                                                    .map(b => typeof b === 'string' ? b.trim().replace(/^[•·\-]\s*/, '') : b)
                                                    .slice(0, 2);
                                                return cleanedBenefits.join(' · ') || 'Daily Use';
                                            })()}
                                        </p>
                                    </div>
                                )}

                                {/* Fragrance */}
                                <div>
                                        <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Fragrance</h3>
                                        <p className="text-gray-700 font-normal text-sm">
                                        {(() => {
                                            let fragranceValue = 'Unscented';
                                            if (Array.isArray(product.fragrance) && product.fragrance.length > 0) {
                                                fragranceValue = product.fragrance[0];
                                            } else if (typeof product.fragrance === 'string' && product.fragrance.trim()) {
                                                fragranceValue = product.fragrance;
                                            } else if (product.attributes?.fragrance) {
                                                fragranceValue = product.attributes.fragrance;
                                            }
                                            return fragranceValue;
                                        })()}
                                    </p>
                                </div>
                            </div>
                            </div>

                            {/* Product Description */}
                            {product.description && (
                                <div className="bg-white rounded-lg p-5 border border-gray-100">
                                    <h4 className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">Description</h4>
                                    <p className="text-gray-600 leading-relaxed text-sm">
                                        {product.description}
                                    </p>
                                </div>
                            )}
                        </div>
                        {/* End of LEFT COLUMN */}

                        {/* RIGHT COLUMN: CTA + Editorial Content */}
                        <div className="space-y-6">
                            {/* Rating & CTA Section - AT THE TOP */}
                            <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-xl p-6 shadow-sm">
                                {/* Rating */}
                                {product.rating && (
                                    <div className="mb-6">
                                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide block text-center mb-2">
                                            Radiance Rating
                                        </span>
                                        <div className="flex items-center justify-center gap-3">
                                            <div className="flex items-center">
                                                {renderStars(product.rating)}
                                            </div>
                                            <span className="text-xl font-bold text-[#ab3d22]">
                                                {Number(product.rating).toFixed(1)}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {/* CTA Buttons - Prominent Position */}
                                <div className="flex flex-col gap-3">
                                    <a
                                        href={product.affiliateUrl || '#'}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center justify-center gap-2 bg-[#ab3d22] text-white px-8 py-4 rounded-lg text-base font-bold hover:bg-[#8a2f1a] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#ab3d22] focus:ring-offset-2 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                                        data-cta="shop_now"
                                        data-product={generateBrandedProductSlug(product.brand, product.name)}
                                        data-category={typeof product.category === 'object' ? product.category?.slug : (product.category || '').toLowerCase().replace(/\s+/g, '-')}
                                    >
                                        Shop Now
                                        <ExternalLink className="h-5 w-5" />
                                    </a>
                                    <button
                                        onClick={() => {
                                            const communitySection = document.getElementById('community-section');
                                            if (communitySection) {
                                                communitySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                setTimeout(() => {
                                                    communitySection.classList.add('highlight-pulse');
                                                    setTimeout(() => {
                                                        communitySection.classList.remove('highlight-pulse');
                                                    }, 1500);
                                                }, 500);
                                            }
                                        }}
                                        className="inline-flex items-center justify-center gap-2 bg-white text-gray-700 px-6 py-3 rounded-lg text-sm font-semibold border-2 border-gray-300 hover:border-[#ab3d22] hover:text-[#ab3d22] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#ab3d22] focus:ring-offset-2"
                                    >
                                        See what others are saying →
                                    </button>
                                </div>
                            </div>

                            {/* RADIANCE TAKE - Below CTA */}
                            {product.radianceTake && (
                                <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-3">
                                    {/* Header */}
                                    <h3 className="text-lg font-semibold text-gray-800" style={{fontFamily: 'Poppins, sans-serif'}}>
                                        Radiance Take
                                    </h3>

                                    {/* Full Radiance Take with Read More for long content */}
                                    {product.radianceTake.length > 200 ? (
                                        <details className="group">
                                            <summary className="cursor-pointer list-none">
                                                <p className="text-gray-700 text-base leading-relaxed inline">
                                                    {product.radianceTake.substring(0, 200)}
                                                    <span className="text-[#ab3d22] font-medium text-sm ml-1 group-open:hidden">
                                                        ... Read more ▼
                                                    </span>
                                                    <span className="hidden group-open:inline">
                                                        {product.radianceTake.substring(200)}
                                                    </span>
                                                </p>
                                                <span className="text-[#ab3d22] font-medium text-sm hidden group-open:inline cursor-pointer ml-1">
                                                    Show less ▲
                                                </span>
                                            </summary>
                                        </details>
                                    ) : (
                                        <p className="text-gray-700 text-base leading-relaxed">
                                            {product.radianceTake}
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Expandable Sections - Under Radiance Take */}
                            <div className="space-y-4">
                                {/* Highlights Section */}
                                {product.highlights && product.highlights.length > 0 && (
                                    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                                        <button
                                            onClick={() => toggleSection('highlights')}
                                            className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors text-left"
                                        >
                                            <div className="flex items-center gap-3">
                                                <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"></path>
                                                </svg>
                                                <h3 className="font-semibold text-gray-900">Highlights</h3>
                                            </div>
                                            <svg className={`w-5 h-5 text-gray-500 transform transition-transform ${expandedSections.highlights ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                            </svg>
                                        </button>
                                        {expandedSections.highlights && (
                                            <div className="px-5 pb-4 border-t border-gray-200 bg-gray-50">
                                                <ul className="space-y-2 pt-4">
                                                    {product.highlights.map((highlight, index) => (
                                                        <li key={index} className="flex items-start gap-2 text-gray-700">
                                                            <span className="text-[#ab3d22] mt-0.5">•</span>
                                                            <span>{highlight}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Pros & Cons Section */}
                                {((product.pros && product.pros.length > 0) || (product.cons && product.cons.length > 0)) && (
                                    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                                        <button
                                            onClick={() => toggleSection('proscons')}
                                            className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors text-left"
                                        >
                                            <div className="flex items-center gap-3">
                                                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path>
                                                </svg>
                                                <h3 className="font-semibold text-gray-900">Pros & Cons</h3>
                                            </div>
                                            <svg className={`w-5 h-5 text-gray-500 transform transition-transform ${expandedSections.proscons ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                            </svg>
                                        </button>
                                        {expandedSections.proscons && (
                                            <div className="px-5 pb-4 border-t border-gray-200 bg-gray-50">
                                                <div className="grid grid-cols-1 gap-6 pt-4">
                                                    {product.pros && product.pros.length > 0 && (
                                                        <div>
                                                            <h4 className="font-semibold text-green-700 mb-2">Pros</h4>
                                                            <ul className="space-y-2">
                                                                {product.pros.map((pro, index) => (
                                                                    <li key={index} className="text-gray-700 flex items-start gap-2 text-sm">
                                                                        <span className="text-green-500 mt-0.5">•</span>
                                                                        <span>{pro}</span>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}
                                                    {product.cons && product.cons.length > 0 && (
                                                        <div>
                                                            <h4 className="font-semibold text-red-700 mb-2">Cons</h4>
                                                            <ul className="space-y-2">
                                                                {product.cons.map((con, index) => (
                                                                    <li key={index} className="text-gray-700 flex items-start gap-2 text-sm">
                                                                        <span className="text-red-500 mt-0.5">•</span>
                                                                        <span>{con}</span>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Ingredient Breakdown Section */}
                                {product.keyIngredients && product.keyIngredients.length > 0 && (
                                    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                                        <button
                                            onClick={() => toggleSection('ingredients')}
                                            className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors text-left"
                                        >
                                            <div className="flex items-center gap-3">
                                                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                                                </svg>
                                                <h3 className="font-semibold text-gray-900">Ingredient Breakdown</h3>
                                            </div>
                                            <svg className={`w-5 h-5 text-gray-500 transform transition-transform ${expandedSections.ingredients ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                            </svg>
                                        </button>
                                        {expandedSections.ingredients && (
                                            <div className="px-5 pb-4 border-t border-gray-200 bg-gray-50">
                                                <ul className="space-y-2 pt-4">
                                                    {product.keyIngredients.map((ingredient, index) => {
                                                        let displayText = '';
                                                        if (typeof ingredient === 'string') {
                                                            displayText = ingredient;
                                                        } else if (ingredient && typeof ingredient === 'object') {
                                                            if (ingredient.name) {
                                                                displayText = ingredient.name;
                                                                if (ingredient.benefit) {
                                                                    displayText += ` → ${ingredient.benefit}`;
                                                                }
                                                            } else if (ingredient._id) {
                                                                const values = Object.entries(ingredient)
                                                                    .filter(([key, value]) => key !== '_id' && value)
                                                                    .map(([key, value]) => value);
                                                                displayText = values.join(' → ');
                                                            }
                                                        }
                                                        
                                                        return (
                                                            <li key={index} className="flex items-start gap-2 text-gray-700">
                                                                <span className="text-[#ab3d22] mt-0.5">•</span>
                                                                <span>{displayText || 'Unknown ingredient'}</span>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        {/* End of RIGHT COLUMN */}
                    </div>
                    {/* End of grid */}

                    {/* Community Reviews Section - Inside Container */}
                    <div id="community-section" className="relative overflow-hidden">
                        {/* Flowing transition from white card to reviews section */}
                        <div className="absolute inset-0 bg-gradient-to-b from-white via-[#faf8f6] to-[#f5f0eb]"></div>
                        
                        {/* Subtle decorative elements for depth */}
                        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-white to-transparent z-10"></div>
                        <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#ab3d22]/[0.03] rounded-full blur-3xl"></div>
                        <div className="absolute top-40 -left-20 w-48 h-48 bg-[#ab3d22]/[0.02] rounded-full blur-2xl"></div>
                        
                        <div className="relative z-20 px-8 pt-8 pb-10">
                            {/* Section Header - More prominent */}
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 bg-gradient-to-br from-[#ab3d22] to-[#8a2f1a] rounded-xl flex items-center justify-center shadow-lg shadow-[#ab3d22]/20">
                                    <span className="text-white text-lg">💬</span>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900" style={{fontFamily: 'Poppins, sans-serif', fontWeight: 700}}>
                                        Community Reviews
                                    </h3>
                                    <p className="text-gray-500 text-sm">
                                        Real experiences from people like you
                                    </p>
                                </div>
                            </div>
                            
                            <CommunityReviewSection 
                                productId={product._id} 
                                productCategory={product.category}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Similar Products Carousel */}
            <SimilarProducts 
                products={similarProducts}
                currentProductId={product?._id}
                title="Shop Similar Products"
                subtitle="Discover other products you might love based on your interests"
            />

            <Footer />
        </div>
    );
};

export default ProductDetailPage;


